/**
 * Legal Disclosures Component
 * ===========================
 * Required legal acknowledgments before account creation.
 * - Age verification notice (18+)
 * - Under 21 alcohol restrictions notice
 * - Terms of Service acknowledgment
 * - Privacy Policy acknowledgment
 * - Platform disclaimer
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Shield, FileText, AlertTriangle, Check, 
  ChevronDown, ChevronUp, ExternalLink, Wine
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LegalDisclosuresStep = ({ 
  userId, 
  isUnder21 = false, 
  age = null,
  onComplete 
}) => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Disclosure states
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [ageVerificationAccepted, setAgeVerificationAccepted] = useState(false);
  const [platformDisclaimerAccepted, setPlatformDisclaimerAccepted] = useState(false);
  const [under21AlcoholNoticeAccepted, setUnder21AlcoholNoticeAccepted] = useState(false);
  
  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const allRequiredAccepted = () => {
    const baseRequired = termsAccepted && privacyAccepted && 
                         ageVerificationAccepted && platformDisclaimerAccepted;
    
    if (isUnder21) {
      return baseRequired && under21AlcoholNoticeAccepted;
    }
    return baseRequired;
  };

  const handleSubmit = async () => {
    if (!allRequiredAccepted()) {
      setError('Please accept all required disclosures to continue');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('nearU_token') || userId;
      
      await axios.post(
        `${API}/identity/accept-legal-disclosures`,
        {
          terms_accepted: termsAccepted,
          privacy_accepted: privacyAccepted,
          age_verification_accepted: ageVerificationAccepted,
          platform_disclaimer_accepted: platformDisclaimerAccepted,
          under_21_alcohol_notice_accepted: isUnder21 ? under21AlcoholNoticeAccepted : null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (onComplete) {
        onComplete();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save disclosures');
    } finally {
      setSubmitting(false);
    }
  };

  const DisclosureItem = ({ 
    id, 
    icon: Icon, 
    title, 
    summary, 
    fullText, 
    checked, 
    onChange, 
    required = true,
    linkUrl,
    linkText 
  }) => (
    <div style={{
      background: 'rgba(26, 15, 10, 0.6)',
      borderRadius: '12px',
      border: checked 
        ? '1px solid rgba(76, 175, 80, 0.4)' 
        : '1px solid rgba(165, 124, 51, 0.2)',
      marginBottom: '12px',
      overflow: 'hidden',
      transition: 'border-color 0.2s ease'
    }}>
      <div 
        style={{
          padding: '16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          cursor: 'pointer'
        }}
        onClick={() => toggleSection(id)}
      >
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          background: checked 
            ? 'rgba(76, 175, 80, 0.15)' 
            : 'rgba(215, 181, 109, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <Icon size={20} color={checked ? '#4CAF50' : '#D7B56D'} />
        </div>
        
        <div style={{ flex: 1 }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginBottom: '4px'
          }}>
            <span style={{ 
              color: '#E6D7C3', 
              fontSize: '15px', 
              fontWeight: '600' 
            }}>
              {title}
            </span>
            {required && (
              <span style={{ 
                color: '#EF4444', 
                fontSize: '11px',
                fontWeight: '500'
              }}>
                Required
              </span>
            )}
          </div>
          <p style={{ 
            color: '#A57C33', 
            fontSize: '13px', 
            margin: 0,
            lineHeight: '1.4'
          }}>
            {summary}
          </p>
        </div>
        
        <div style={{ color: '#8A7355' }}>
          {expandedSections[id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>

      {expandedSections[id] && (
        <div style={{
          padding: '0 16px 16px 68px',
          borderTop: '1px solid rgba(165, 124, 51, 0.1)'
        }}>
          <div style={{
            padding: '12px',
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '8px',
            marginTop: '12px',
            maxHeight: '200px',
            overflowY: 'auto'
          }}>
            <p style={{ 
              color: '#B8A589', 
              fontSize: '12px', 
              margin: 0,
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap'
            }}>
              {fullText}
            </p>
          </div>
          
          {linkUrl && (
            <a
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                color: '#D7B56D',
                fontSize: '13px',
                marginTop: '12px',
                textDecoration: 'none'
              }}
            >
              {linkText || 'Read full document'}
              <ExternalLink size={14} />
            </a>
          )}
        </div>
      )}

      <div style={{
        padding: '12px 16px',
        background: 'rgba(0, 0, 0, 0.2)',
        borderTop: '1px solid rgba(165, 124, 51, 0.1)'
      }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          cursor: 'pointer'
        }}>
          <div 
            onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              border: checked 
                ? '2px solid #4CAF50' 
                : '2px solid rgba(165, 124, 51, 0.4)',
              background: checked ? '#4CAF50' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              cursor: 'pointer'
            }}
          >
            {checked && <Check size={14} color="#FFF" />}
          </div>
          <span style={{ 
            color: '#E6D7C3', 
            fontSize: '14px' 
          }}>
            I acknowledge and accept
          </span>
        </label>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{
        textAlign: 'center',
        marginBottom: '24px'
      }}>
        <div style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #D7B56D, #A57C33)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px'
        }}>
          <Shield size={28} color="#1A0F0A" />
        </div>
        <h2 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '20px',
          color: '#E6D7C3',
          marginBottom: '8px'
        }}>
          Legal Disclosures
        </h2>
        <p style={{
          color: '#A57C33',
          fontSize: '14px'
        }}>
          Please review and accept the following to continue
        </p>
      </div>

      {/* Age Verification Notice */}
      <DisclosureItem
        id="age-verification"
        icon={Shield}
        title="Age Verification Notice"
        summary="I confirm I am 18 years of age or older"
        fullText={`AGE VERIFICATION NOTICE

By using NearU, you confirm that:

• You are at least 18 years of age
• You have provided accurate date of birth information during identity verification
• You understand that providing false age information is grounds for immediate account termination
• You acknowledge that NearU uses government-issued ID verification to confirm your age

NearU is designed for adults aged 18 and older. We verify all users' ages through our identity verification process using Stripe Identity.

If you are found to have misrepresented your age, your account will be permanently banned and you may be reported to relevant authorities.`}
        checked={ageVerificationAccepted}
        onChange={setAgeVerificationAccepted}
        linkUrl="/terms#age-verification"
        linkText="View age policy"
      />

      {/* Under 21 Alcohol Notice - Only shown if under 21 */}
      {isUnder21 && (
        <DisclosureItem
          id="under-21-alcohol"
          icon={Wine}
          title="Under 21 Feature Restrictions"
          summary={`As you are ${age} years old, certain features are restricted`}
          fullText={`UNDER 21 ALCOHOL-RELATED FEATURE RESTRICTIONS

As a member under 21 years of age, the following features are restricted:

RESTRICTED FEATURES:
• Sending drinks to other members
• Ordering bottle service through NearU Concierge
• Booking reservations at bars or venues that primarily serve alcohol
• Accessing alcohol-related gift options

ADDITIONAL REQUIREMENTS:
• Your age will be displayed on your profile (this cannot be hidden)
• Other members will be able to see that you are under 21

WHY THESE RESTRICTIONS EXIST:
NearU complies with all applicable laws regarding the purchase, service, and promotion of alcoholic beverages. These restrictions are in place to ensure legal compliance and protect our community.

These restrictions will automatically be lifted when you turn 21.`}
          checked={under21AlcoholNoticeAccepted}
          onChange={setUnder21AlcoholNoticeAccepted}
          required={true}
        />
      )}

      {/* Terms of Service */}
      <DisclosureItem
        id="terms"
        icon={FileText}
        title="Terms of Service"
        summary="I agree to the NearU Terms of Service"
        fullText={`TERMS OF SERVICE SUMMARY

By accepting these terms, you agree to:

1. ELIGIBILITY
   • You must be 18 years or older to use NearU
   • You must provide accurate information during registration
   • You must complete identity verification before accessing member features

2. ACCEPTABLE USE
   • Treat all members with respect
   • Do not engage in harassment, hate speech, or illegal activities
   • Do not create fake profiles or misrepresent yourself
   • Do not share explicit content

3. PRIVACY & SAFETY
   • Your identity and personal information are protected
   • Report any suspicious or inappropriate behavior
   • NearU may share information with law enforcement when required

4. MEMBERSHIP & PAYMENTS
   • Subscription fees are charged in advance
   • Cancellation does not provide refunds for unused periods
   • Gift transactions are final and non-refundable

5. TERMINATION
   • NearU may terminate accounts that violate these terms
   • Users may delete their accounts at any time`}
        checked={termsAccepted}
        onChange={setTermsAccepted}
        linkUrl="/terms"
        linkText="Read full Terms of Service"
      />

      {/* Privacy Policy */}
      <DisclosureItem
        id="privacy"
        icon={Shield}
        title="Privacy Policy"
        summary="I acknowledge the NearU Privacy Policy"
        fullText={`PRIVACY POLICY SUMMARY

DATA WE COLLECT:
• Identity information (name, date of birth, government ID)
• Contact information (email, phone number)
• Profile information (photos, bio, preferences)
• Location data (when using Proximity features)
• Usage data (app interactions, matches, messages)

HOW WE USE YOUR DATA:
• To verify your identity and age
• To match you with compatible members
• To enable Proximity and other features
• To improve our services
• To send important notifications

DATA SHARING:
• We share data with Stripe for identity verification
• We may share data with law enforcement when legally required
• We do not sell your personal data to third parties

YOUR RIGHTS:
• Access your personal data
• Request data deletion
• Opt out of marketing communications
• Control visibility of profile information

DATA SECURITY:
• All data is encrypted in transit and at rest
• Identity documents are processed securely and not stored
• We implement industry-standard security measures`}
        checked={privacyAccepted}
        onChange={setPrivacyAccepted}
        linkUrl="/privacy"
        linkText="Read full Privacy Policy"
      />

      {/* Platform Disclaimer */}
      <DisclosureItem
        id="disclaimer"
        icon={AlertTriangle}
        title="Platform Disclaimer"
        summary="I understand the platform limitations and risks"
        fullText={`PLATFORM DISCLAIMER

SAFETY NOTICE:
• NearU verifies identities but cannot guarantee the character or intentions of any member
• Always meet new connections in public places
• Trust your instincts and report suspicious behavior
• NearU is not responsible for interactions between members

NO GUARANTEE OF MATCHES:
• NearU cannot guarantee you will find compatible matches
• Success depends on many factors including your engagement and profile

GIFT TRANSACTIONS:
• All gift transactions are final
• NearU is not responsible for disputes between members regarding gifts
• Gift monetary value is determined by NearU and may change

PREMIUM FEATURES:
• Certain features require paid membership
• Premium features may change or be discontinued
• Refunds are handled according to our refund policy

LIMITATION OF LIABILITY:
• NearU is provided "as is" without warranties
• We are not liable for indirect, incidental, or consequential damages
• Our liability is limited to the amount you paid for services

By using NearU, you acknowledge these limitations and agree to use the platform responsibly.`}
        checked={platformDisclaimerAccepted}
        onChange={setPlatformDisclaimerAccepted}
      />

      {/* Error Display */}
      {error && (
        <div style={{
          padding: '14px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <AlertTriangle size={18} color="#EF4444" />
          <span style={{ color: '#EF4444', fontSize: '14px' }}>{error}</span>
        </div>
      )}

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        disabled={!allRequiredAccepted() || submitting}
        data-testid="accept-disclosures-btn"
        style={{
          width: '100%',
          padding: '16px',
          background: allRequiredAccepted()
            ? 'linear-gradient(135deg, #D7B56D, #A57C33)'
            : 'rgba(165, 124, 51, 0.3)',
          border: 'none',
          borderRadius: '12px',
          color: allRequiredAccepted() ? '#1A0F0A' : '#6B5B4D',
          fontSize: '16px',
          fontWeight: '600',
          cursor: allRequiredAccepted() ? 'pointer' : 'not-allowed',
          opacity: submitting ? 0.7 : 1
        }}
      >
        {submitting ? 'Processing...' : 'Accept & Continue'}
      </button>

      <p style={{
        color: '#6B5B4D',
        fontSize: '12px',
        textAlign: 'center',
        marginTop: '16px',
        lineHeight: '1.5'
      }}>
        By clicking "Accept & Continue", you agree to all disclosures marked as required.
        These agreements are binding and form part of your account terms.
      </p>
    </div>
  );
};

export default LegalDisclosuresStep;
